import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Alert, Card, CardContent,
  Stack, Chip, LinearProgress, TextField,
} from '@mui/material';
import { Upload, CheckCircle, HourglassEmpty, Error as ErrorIcon } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DynamicFormRenderer from '../../components/dynamic/DynamicFormRenderer';
import { getFormByCode } from '../../services/platform/runtimeService';
import {
  getOnboardingStatus, submitPartnerOnboarding, uploadPartnerDocument,
} from '../../services/partnerOnboardingService';
import { documentConfigIdFromRef } from '../../utils/documentReviewUtils';

const FORM_TITLES: Record<string, string> = {
  driver_onboarding: 'Driver Registration',
  delivery_partner_onboarding: 'Delivery Partner Onboarding',
  merchant_onboarding: 'Merchant Onboarding',
  service_provider_onboarding: 'Service Provider',
};

const DRIVER_STEPS = ['Personal & Vehicle', 'KYC & Vehicle Docs', 'Verification Status'];
const DEFAULT_STEPS = ['Application Form', 'Upload Documents', 'Status'];

export default function PartnerOnboardingFlowPage() {
  const { formCode } = useParams<{ formCode: string }>();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  if (!formCode) return <Navigate to="/onboarding" replace />;

  const { data: form, isLoading: formLoading, error: formError } = useQuery({
    queryKey: ['form-by-code', formCode],
    queryFn: () => getFormByCode(formCode),
  });

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['partner-onboarding-status', formCode],
    queryFn: () => getOnboardingStatus(formCode),
    enabled: !!formCode,
  });

  const submitMut = useMutation({
    mutationFn: (payload: { formCode: string; formData: Record<string, unknown> }) =>
      submitPartnerOnboarding(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-onboarding-status', formCode] });
      setStep(1);
    },
  });

  useEffect(() => {
    if (!status?.record) return;
    if (status.record.status === 'approved') setStep(2);
    else if (['submitted', 'documents_pending', 'documents_verified'].includes(status.record.status)) setStep(1);
  }, [status?.record?.status]);

  const handleUpload = async (configId: string, file: File, docNumber?: string, expiry?: string) => {
    setUploadingId(configId);
    try {
      await uploadPartnerDocument(file, configId, { documentNumber: docNumber, expiryDate: expiry });
      await refetchStatus();
    } finally {
      setUploadingId(null);
    }
  };

  const isApproved = status?.record?.status === 'approved';
  const approvalRequired = status?.approvalRequired ?? true;
  const documents = status?.documents || [];
  const userDocMap = new Map(
    (status?.compliance?.userDocs || []).map((d) => [documentConfigIdFromRef(d.documentConfigId), d]),
  );

  const steps = formCode === 'driver_onboarding' ? DRIVER_STEPS : DEFAULT_STEPS;

  return (
    <PageShell maxWidth={800}>
      <PageHeader
        gradient
        eyebrow="Partner Onboarding"
        title={FORM_TITLES[formCode] || form?.name || 'Onboarding'}
        subtitle={
          approvalRequired
            ? 'Complete the form and upload required documents. Your account activates after verification.'
            : 'Complete the form to onboard. Document verification is not required.'
        }
      />

      <Stepper activeStep={isApproved ? 2 : step} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {(formLoading || statusLoading) && <LinearProgress sx={{ mb: 2 }} />}

      {formError && <Alert severity="error">Form not found: {formCode}</Alert>}

      {step === 0 && form && !isApproved && (
        <Box>
          <DynamicFormRenderer
            form={form}
            submitLabel={formCode === 'driver_onboarding' ? 'Submit for Verification' : 'Continue to Documents'}
            onSuccess={(data) => {
              setFormData(data);
              submitMut.mutate({ formCode, formData: data });
            }}
          />
          {submitMut.isError && <Alert severity="error" sx={{ mt: 2 }}>Failed to submit application</Alert>}
        </Box>
      )}

      {step === 1 && !isApproved && (
        <Box>
          {status?.group && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Document group: <strong>{status.group.name}</strong>
            </Alert>
          )}

          {documents.length === 0 ? (
            <Alert severity="warning">
              No documents configured for this partner type. Contact admin or proceed once form is submitted.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {documents.map((doc) => {
                const uploaded = userDocMap.get(doc._id);
                const st = uploaded?.status;
                return (
                  <Card key={doc._id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box>
                          <Typography fontWeight={700}>{doc.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doc.isMandatory ? 'Mandatory' : 'Optional'}
                            {doc.expiryRequired ? ' · Expiry required' : ''}
                          </Typography>
                        </Box>
                        {st === 'approved' && <Chip icon={<CheckCircle />} label="Verified" color="success" size="small" />}
                        {st === 'pending' && <Chip icon={<HourglassEmpty />} label="Pending review" color="warning" size="small" />}
                        {st === 'rejected' && <Chip icon={<ErrorIcon />} label="Rejected" color="error" size="small" />}
                      </Stack>

                      {st === 'rejected' && uploaded?.rejectionReason && (
                        <Alert severity="error" sx={{ mb: 1 }}>{uploaded.rejectionReason}</Alert>
                      )}

                      {(!st || st === 'rejected') && (
                        <Stack spacing={1.5}>
                          <TextField size="small" label="Document number (optional)" id={`num-${doc._id}`} />
                          {doc.expiryRequired && (
                            <TextField size="small" type="date" label="Expiry date" InputLabelProps={{ shrink: true }} id={`exp-${doc._id}`} />
                          )}
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<Upload />}
                            disabled={uploadingId === doc._id}
                          >
                            {uploadingId === doc._id ? 'Uploading...' : st === 'rejected' ? 'Re-upload' : 'Upload'}
                            <input
                              hidden
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const num = (document.getElementById(`num-${doc._id}`) as HTMLInputElement)?.value;
                                const exp = (document.getElementById(`exp-${doc._id}`) as HTMLInputElement)?.value;
                                handleUpload(doc._id, file, num, exp);
                              }}
                            />
                          </Button>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          <Stack direction="row" spacing={2} mt={3}>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button variant="contained" onClick={() => setStep(2)}>View Status</Button>
          </Stack>
        </Box>
      )}

      {(step === 2 || isApproved) && status && (
        <Box>
          {isApproved ? (
            <Alert severity="success" icon={<CheckCircle />}>
              Onboarding complete! Your partner account is active.
            </Alert>
          ) : approvalRequired ? (
            status.compliance.allMandatoryApproved ? (
              <Alert severity="success">All documents verified. Awaiting final activation.</Alert>
            ) : status.compliance.allMandatoryUploaded ? (
              <Alert severity="info" icon={<HourglassEmpty />}>
                Documents uploaded. Waiting for admin verification before you can onboard.
              </Alert>
            ) : (
              <Alert severity="warning">
                Upload all mandatory documents to continue ({status.compliance.missing.length} remaining).
              </Alert>
            )
          ) : (
            <Alert severity="success">
              Document verification is disabled. Your application has been submitted.
            </Alert>
          )}

          <Card sx={{ mt: 2 }} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Application status</Typography>
              <Typography variant="h6" fontWeight={700} textTransform="capitalize">
                {status.record?.status?.replace(/_/g, ' ') || 'Not started'}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Document approval: {approvalRequired ? 'Required' : 'Not required'}
              </Typography>
            </CardContent>
          </Card>

          {!isApproved && (
            <Stack direction="row" spacing={2} mt={3}>
              <Button onClick={() => setStep(1)}>Back to Documents</Button>
              <Button variant="outlined" onClick={() => refetchStatus()}>Refresh</Button>
            </Stack>
          )}
        </Box>
      )}
    </PageShell>
  );
}
